import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import { Label } from '@/components/ui/shadcn/label'
import { Input } from '@/components/ui/shadcn/input'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { trpc } from '@/config/trpc'
import type { WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { ChevronUp, ChevronDown } from 'lucide-react'

const TRANSITION_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'fadeblack', label: 'Fade to Black' },
  { value: 'fadewhite', label: 'Fade to White' },
  { value: 'dissolve', label: 'Dissolve' },
  { value: 'wipeleft', label: 'Wipe Left' },
  { value: 'wiperight', label: 'Wipe Right' },
  { value: 'wipeup', label: 'Wipe Up' },
  { value: 'wipedown', label: 'Wipe Down' },
  { value: 'slideleft', label: 'Slide Left' },
  { value: 'slideright', label: 'Slide Right' },
  { value: 'slideup', label: 'Slide Up' },
  { value: 'slidedown', label: 'Slide Down' },
] as const

const ASPECT_RATIOS = [
  { value: '9:16', label: '9:16 (Reels/Stories)', width: 1080, height: 1920 },
  { value: '1:1', label: '1:1 (Square)', width: 1080, height: 1080 },
  { value: '16:9', label: '16:9 (Landscape)', width: 1920, height: 1080 },
] as const

type TransitionType = (typeof TRANSITION_TYPES)[number]['value']
type AspectRatio = (typeof ASPECT_RATIOS)[number]['value']

interface SelectableMedia {
  media: WorkspaceMedia
  selected: boolean
  originalIndex: number
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceMedia: WorkspaceMedia[]
  workspaceId: number
  onVideoGenerated: (videoUrl: string, key: string, sizeBytes: number) => Promise<void>
}

export function VideoSlideshowDialog({
  open,
  onOpenChange,
  workspaceMedia,
  workspaceId,
  onVideoGenerated,
}: Props) {
  // Filter to only images and canvases (excludes generating placeholders)
  const imageMedia = useMemo(
    () =>
      workspaceMedia.filter(
        (m): m is WorkspaceMedia & { type: 'image' | 'canvas' } =>
          m.type === 'image' || m.type === 'canvas',
      ),
    [workspaceMedia],
  )

  // Selection and ordering state
  const [selectableMedia, setSelectableMedia] = useState<SelectableMedia[]>(() =>
    imageMedia.map((media, index) => ({
      media,
      selected: true,
      originalIndex: index,
    })),
  )

  // Reset selection when media changes
  const [prevImageMedia, setPrevImageMedia] = useState(imageMedia)
  if (prevImageMedia !== imageMedia) {
    setPrevImageMedia(imageMedia)
    setSelectableMedia(
      imageMedia.map((media, index) => ({
        media,
        selected: true,
        originalIndex: index,
      })),
    )
  }

  // Form state
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('9:16')
  const [imageDuration, setImageDuration] = useState(3)
  const [transitionType, setTransitionType] = useState<TransitionType>('fade')
  const [transitionDuration, setTransitionDuration] = useState(0.5)

  const generateMutation = trpc.video.generateSlideshow.useMutation()

  const selectedMedia = selectableMedia.filter((m) => m.selected)
  const canGenerate = selectedMedia.length > 0

  const toggleSelection = (index: number) => {
    setSelectableMedia((prev) =>
      prev.map((item, i) => (i === index ? { ...item, selected: !item.selected } : item)),
    )
  }

  const moveUp = (index: number) => {
    if (index === 0) return
    setSelectableMedia((prev) => {
      const newArr = [...prev]
      const current = newArr[index]
      const above = newArr[index - 1]
      if (current && above) {
        newArr[index - 1] = current
        newArr[index] = above
      }
      return newArr
    })
  }

  const moveDown = (index: number) => {
    if (index === selectableMedia.length - 1) return
    setSelectableMedia((prev) => {
      const newArr = [...prev]
      const current = newArr[index]
      const below = newArr[index + 1]
      if (current && below) {
        newArr[index] = below
        newArr[index + 1] = current
      }
      return newArr
    })
  }

  const handleGenerate = async () => {
    const aspectConfig = ASPECT_RATIOS.find((a) => a.value === aspectRatio)!

    try {
      const result = await generateMutation.mutateAsync({
        workspaceId,
        images: selectedMedia.map((item) => ({
          url: item.media.file.url,
          duration: imageDuration,
        })),
        transition: {
          type: transitionType,
          duration: transitionDuration,
        },
        output: {
          width: aspectConfig.width,
          height: aspectConfig.height,
          fps: 30,
        },
      })

      await onVideoGenerated(result.videoUrl, result.key, result.sizeBytes)
      toast.success(`Video generated! Duration: ${result.durationSeconds.toFixed(1)}s`)
      onOpenChange(false)
    } catch (error) {
      console.error('Video generation failed:', error)
      toast.error('Failed to generate video. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Generate Slideshow Video</DialogTitle>
          <DialogDescription>
            Select and arrange images to create a video slideshow
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Image Selection Grid */}
          <div className="space-y-2">
            <Label>Images ({selectedMedia.length} selected)</Label>
            <div className="border-border max-h-[200px] overflow-y-auto rounded-md border p-2">
              <div className="space-y-2">
                {selectableMedia.map((item, index) => (
                  <div
                    key={item.originalIndex}
                    className={cn(
                      'flex items-center gap-3 rounded-md p-2',
                      item.selected ? 'bg-primary/10' : 'bg-muted',
                    )}>
                    <Checkbox
                      checked={item.selected}
                      onCheckedChange={() => toggleSelection(index)}
                    />
                    <img
                      src={item.media.file.url}
                      crossOrigin="anonymous"
                      alt={`Image ${index + 1}`}
                      className="h-12 w-12 rounded object-cover"
                    />
                    <span className="text-muted-foreground flex-1 text-sm">
                      {item.selected ? `#${selectedMedia.indexOf(item) + 1}` : 'Not selected'}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => moveUp(index)}
                        disabled={index === 0}>
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => moveDown(index)}
                        disabled={index === selectableMedia.length - 1}>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Settings Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="aspect-ratio">Aspect Ratio</Label>
              <Select value={aspectRatio} onValueChange={(v) => setAspectRatio(v as AspectRatio)}>
                <SelectTrigger id="aspect-ratio">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASPECT_RATIOS.map((ratio) => (
                    <SelectItem key={ratio.value} value={ratio.value}>
                      {ratio.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image-duration">Duration per Image (s)</Label>
              <Input
                id="image-duration"
                type="number"
                min={1}
                max={10}
                step={0.5}
                value={imageDuration}
                onChange={(e) => setImageDuration(parseFloat(e.target.value) || 3)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="transition-type">Transition</Label>
              <Select
                value={transitionType}
                onValueChange={(v) => setTransitionType(v as TransitionType)}>
                <SelectTrigger id="transition-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSITION_TYPES.map((transition) => (
                    <SelectItem key={transition.value} value={transition.value}>
                      {transition.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transition-duration">Transition Duration (s)</Label>
              <Input
                id="transition-duration"
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={transitionDuration}
                onChange={(e) => setTransitionDuration(parseFloat(e.target.value) || 0.5)}
                disabled={transitionType === 'none'}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={!canGenerate || generateMutation.isPending}>
            {generateMutation.isPending ? 'Generating...' : 'Generate Video'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
